import { useApprovingInvoiceReportApprovalRoute } from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.service';
import { useApprovingInvoiceReportsSearchRefetch } from '@/context/services/approving_invoice_reports/refetchContext';
import { useCheckApprovingReportsProtectedResources } from '@/features/approving_invoice_reports/components/hooks/useCheckApprovingReportsProtectedResources';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback, useEffect } from 'react';

type UseApprovalRouteProps = {
  reportId: string;
  onCloseDetailDrawer: () => void;
};

export const useApprovalRoute = ({
  reportId,
  onCloseDetailDrawer,
}: UseApprovalRouteProps) => {
  const refetchApprovingInvoiceReportsSearch =
    useApprovingInvoiceReportsSearchRefetch();
  const notify = useToastAPI();
  const { t } = useTranslation();
  const { handleCheckPermission } =
    useCheckApprovingReportsProtectedResources();
  const { data, error } = useApprovingInvoiceReportApprovalRoute(reportId);

  const showErrorMessages = useCallback(
    (messages?: string) => {
      notify.error({
        title: messages || t('system_error'),
        duration: 3,
      });
    },
    [notify, t]
  );

  const onCheckAccessApprovingReportSuccess = useCallback(() => {
    onCloseDetailDrawer();
    showErrorMessages(error?.messages?.join(','));
  }, [onCloseDetailDrawer, error?.messages, showErrorMessages]);

  useEffect(() => {
    if (!error) return;
    if (error?.code === '403' || error?.code === '401') {
      handleCheckPermission({
        fnCheckPermission: refetchApprovingInvoiceReportsSearch,
        onCheckSuccess: onCheckAccessApprovingReportSuccess,
        onCheckFailure: onCloseDetailDrawer,
      });
    } else {
      showErrorMessages(error?.messages?.join(','));
      onCloseDetailDrawer();
    }
  }, [
    error,
    onCloseDetailDrawer,
    refetchApprovingInvoiceReportsSearch,
    handleCheckPermission,
    showErrorMessages,
    onCheckAccessApprovingReportSuccess,
  ]);

  return { data };
};
